/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { startTransition, StrictMode, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";
import * as Sentry from "@sentry/remix";

// Initialize Sentry for client-side error tracking
Sentry.init({
  dsn: window.ENV?.SENTRY_DSN,
  
  // Environment
  environment: window.ENV?.NODE_ENV || "development",
  
  // Performance Monitoring - capture 100% of transactions
  tracesSampleRate: 1.0,
  
  // Session Replay - capture 10% of sessions, 100% on error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  
  integrations: [
    // Browser tracing for performance monitoring
    Sentry.browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches,
    }),
    // Session replay for debugging
    Sentry.replayIntegration({
      // No masking as requested
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
    }),
  ],
  
  // Filter out noisy/irrelevant errors
  ignoreErrors: [
    // Browser extensions
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    // Non-errors
    'Non-Error promise rejection',
    // Network issues (user's connection)
    'Failed to fetch',
    'NetworkError',
    'Load failed',
    // Third-party scripts
    /^Script error\.?$/,
  ],
  
  // Don't track localhost in production builds
  beforeSend(event) {
    // Skip if running locally (unless explicitly testing)
    if (window.location.hostname === 'localhost' && !window.location.search.includes('sentry=true')) {
      return null;
    }
    return event;
  },
});

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>
  );
});
